/**
 * motion.tsx — the shared reduced-motion signal + a tiny mount reveal helper.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: E3 (premium polish).
 *
 * WHY one home: the premium-polish pass animates several surfaces (question and
 * stage transitions, the BMI dial sweep, skeleton loaders). They all gate on the
 * SAME `prefers-reduced-motion` answer and share the same "reveal a frame after
 * mount so the CSS transition actually plays" trick, so both live here once.
 *
 * `usePrefersReducedMotion` is re-exported by {@link ../journey/ProjectionChart}
 * (its original home) so that component's consumers are unchanged — this file is
 * now the single definition and `ProjectionChart` forwards it.
 *
 * REDUCED MOTION is the safe default: when we cannot ask (`matchMedia` missing —
 * e.g. a non-browser test runner) OR the user asked to reduce, we skip the
 * animation and render the finished state on the first paint (no flash). The
 * global reduced-motion kill in tokens.css additionally neutralises any CSS
 * animation/transition for that cohort — these hooks handle the JS-driven
 * initial state that CSS alone cannot (an element must not paint at opacity 0
 * and then snap visible).
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

/** True when we can't ask (no `matchMedia`) or the answer is "reduce". Either way the safe default is: skip the animation. */
export function queryReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Live `prefers-reduced-motion` state. Shared by the projection chart's reveal,
 * the interstitial's headline counter, the BMI dial sweep and the FadeIn/Skeleton
 * primitives so every animation gates on one signal.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(queryReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

/**
 * `false` on the first paint, flipped to `true` a frame later so a CSS
 * transition on the initial "hidden" values actually animates (the hidden state
 * has to paint before the change). Returns `true` immediately when `disabled`
 * (reduced motion / no rAF) so the finished state paints straight away.
 * `resetKey` restarts the reveal when it changes (e.g. a new question key).
 */
export function useRevealAfterMount(disabled: boolean, resetKey?: unknown): boolean {
  const [revealed, setRevealed] = useState(disabled);

  useEffect(() => {
    if (disabled || typeof requestAnimationFrame !== 'function') {
      setRevealed(true);
      return;
    }
    setRevealed(false);
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setRevealed(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [disabled, resetKey]);

  return revealed;
}

/**
 * A light fade + rise (opacity 0→1, translateY 8px→0) played on mount and again
 * whenever `resetKey` changes — used for question and journey-stage transitions.
 *
 * The settled transform is `none` (not `translateY(0)`): a lingering transform
 * would make the wrapper a containing block for any `position: fixed` descendant
 * (e.g. ResultScreen's pinned CTA bar), so it MUST clear once the rise finishes.
 * Reduced-motion → no transform and instant, so nothing ever animates or shifts.
 */
export function FadeIn({
  children,
  resetKey,
  style,
}: {
  children: ReactNode;
  resetKey?: unknown;
  style?: CSSProperties;
}) {
  const reduced = usePrefersReducedMotion();
  const revealed = useRevealAfterMount(reduced, resetKey);
  return (
    <div
      style={{
        opacity: revealed ? 1 : 0,
        transform: reduced ? undefined : revealed ? 'none' : 'translateY(8px)',
        transition: reduced ? undefined : `opacity var(--gps-dur) var(--gps-ease), transform var(--gps-dur) var(--gps-ease)`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
